/**
 * ruleTypeRegistry.js
 *
 * Central registry for condition types. Contains three exports:
 *
 * - conditionComponent(code): returns the async component function for a given
 *   rule_type_code. Falls back to aPricingStrategyRuleTypeConditionBodyGeneric
 *   if no specific file exists.
 *
 * - actionMode(settingsSchema): derives 'market' or 'manual' from the rule type's
 *   settings_schema so no type code is hardcoded anywhere else.
 *
 * - defaultSettings(settingsSchema): computes initial setting values from the schema.
 *
 * Adding a new condition type:
 *   1. Create aPricingStrategyRuleTypeConditionBody-<code>.vue in this folder
 *      (using the field kit).
 *   2. If it shares layout with an existing type, add an alias in CODE_COMPONENT_MAP.
 *   3. That's it — the canvas and drawers pick it up automatically.
 */

// Maps a rule_type_code to a different body component filename.
// Use when two codes share the same UI (e.g. negative variant of pickup).
const CODE_COMPONENT_MAP = {
  "pickup-roomnights-negative": "pickup-roomnights"
};

// Module-level cache so the same function reference is returned for the same
// code. Vue 2 uses function identity to cache async components.
const _componentCache = {};

/**
 * Returns the async component function for a given rule_type_code.
 * Falls back to aPricingStrategyRuleTypeConditionBodyGeneric.vue if no
 * specific body file exists.
 */
export function conditionComponent(code) {
  if (!_componentCache[code]) {
    const resolved = CODE_COMPONENT_MAP[code] || code;
    _componentCache[code] = () =>
      import(
        `./aPricingStrategyRuleTypeConditionBody-${resolved}.vue`
      ).catch(() =>
        import("./aPricingStrategyRuleTypeConditionBodyGeneric.vue")
      );
  }
  return _componentCache[code];
}

/**
 * Derives the action mode from the type's settings_schema.
 * Returns 'market' if the schema declares compset_range, 'manual' otherwise.
 * This keeps all type-specific knowledge inside the registry / schema.
 */
export function actionMode(settingsSchema) {
  if (!settingsSchema) return "manual";
  return "compset_range" in settingsSchema ? "market" : "manual";
}

/**
 * Modos de acción que admite un tipo de regla, en orden de preferencia (el
 * primero es el que se aplica por defecto al elegirlo como disparador).
 *
 * Los tipos pueden declararlo explícitamente con `action_modes` — es lo que
 * hacen las condiciones admitidas en guardarailes, que ofrecen "set_value",
 * "market" y "notify". Si no lo declaran, se mantiene la inferencia de
 * actionMode() sobre el settings_schema, así que los tipos existentes no
 * cambian de comportamiento.
 */
export function actionModesFor(ruleType) {
  const declared = ruleType && ruleType.action_modes;
  if (Array.isArray(declared) && declared.length) return declared;
  return [actionMode(ruleType && ruleType.settings_schema)];
}

/**
 * Returns a descriptor that the summary bar uses to render a single condition
 * in the format: [Title] = value+unit @ spot
 *
 * Descriptor shape (all fields optional):
 *   { valueField, unit, spotField, rangeFrom, rangeTo, enumField }
 *
 * Hard-coded for the known types; falls back to a generic schema-driven
 * derivation so future types work without touching this file.
 */
export function conditionSummaryFields(code, settingsSchema) {
  // Hard-coded known types
  if (code === "pickup-roomnights" || code === "pickup-roomnights-negative") {
    return {
      valueField: "condition_rn",
      unit: "RN",
      spotField: "condition_spot"
    };
  }
  if (code === "occupancy-compset") {
    return {
      rangeFrom: "condition_occupancy_from",
      rangeTo: "condition_occupancy_to",
      unit: "%",
      enumField: "compset_range"
    };
  }

  // Condiciones admitidas en guardarailes
  if (code === "price-lower-than" || code === "price-greater-than") {
    return { valueField: "price", unit: "€" };
  }
  if (code === "compset-range") {
    return { enumField: "condition_compset_range" };
  }
  if (code === "occupancy-range") {
    return {
      rangeFrom: "condition_occupancy_from",
      rangeTo: "condition_occupancy_to",
      unit: "%"
    };
  }

  // Generic fallback: derive from schema
  const schema = settingsSchema || {};
  const keys = Object.keys(schema);
  const descriptor = {};

  // First _from/_to pair → range
  for (const key of keys) {
    if (key.endsWith("_from")) {
      const toKey = key.replace(/_from$/, "_to");
      if (schema[toKey]) {
        descriptor.rangeFrom = key;
        descriptor.rangeTo = toKey;
        break;
      }
    }
  }

  // First numeric field (not already claimed) → valueField
  if (!descriptor.rangeFrom) {
    for (const key of keys) {
      const rules = schema[key] || [];
      const hasMinMax = rules.some(
        r => r && typeof r === "object" && ("min" in r || "max" in r)
      );
      if (hasMinMax) {
        descriptor.valueField = key;
        break;
      }
    }
  }

  // First enum field → spotField / enumField
  for (const key of keys) {
    const rules = schema[key] || [];
    const hasIn = rules.some(r => r && typeof r === "object" && "in" in r);
    if (hasIn) {
      if (descriptor.rangeFrom) {
        descriptor.enumField = key;
      } else {
        descriptor.spotField = key;
      }
      break;
    }
  }

  return descriptor;
}

// Importe de partida de un campo de dinero. El máximo del schema es un tope de
// validación (9999), no una pista de por dónde anda el precio del hotel, así
// que no se puede derivar de él: se arranca en una cifra redonda y baja que el
// usuario cambia de todas formas.
export const DEFAULT_CURRENCY_AMOUNT = 100;

/**
 * Builds default settings values from a rule type's settings_schema.
 * Mirrors the logic previously inlined in the canvas's defaultSettings(code).
 *
 * Rules per field (array):
 *   { required: true }      → ignored for defaults
 *   { default: X }          → wins over everything else
 *   { in: [...] }           → pick second item (or first)
 *   "currency"              → DEFAULT_CURRENCY_AMOUNT, clamped to the range
 *   { min: X, max: Y }      → pick min if > 0, else 80% of max
 * Fields ending in _to      → pick max value
 */
export function defaultSettings(settingsSchema) {
  const schema = settingsSchema || {};
  const defaults = {};
  Object.keys(schema).forEach(field => {
    const rules = schema[field] || [];
    const objectRule = key =>
      Array.isArray(rules) &&
      rules.find(r => r && typeof r === "object" && key in r);
    const minRule = objectRule("min");
    const maxRule = objectRule("max");
    const inRule = objectRule("in");
    const defaultRule = objectRule("default");
    const isCurrency = Array.isArray(rules) && rules.indexOf("currency") > -1;

    if (defaultRule) {
      defaults[field] = defaultRule.default;
    } else if (inRule && inRule.in && inRule.in.length > 0) {
      defaults[field] = inRule.in[1] || inRule.in[0];
    } else if (field.endsWith("_to") && maxRule) {
      defaults[field] = maxRule.max;
    } else if (isCurrency) {
      // El 80% del máximo daría 7999€ para un tope de 9999: un umbral de
      // precio no se parece en nada a su cota de validación.
      const min = minRule ? minRule.min : 0;
      const max = maxRule ? maxRule.max : DEFAULT_CURRENCY_AMOUNT;
      defaults[field] = Math.min(max, Math.max(min, DEFAULT_CURRENCY_AMOUNT));
    } else if (minRule) {
      defaults[field] =
        minRule.min > 0
          ? minRule.min
          : maxRule
          ? Math.round(maxRule.max * 0.8)
          : 0;
    } else {
      defaults[field] = null;
    }
  });
  return defaults;
}
